import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getActiveTrials, getExpiredTrials, activateTrial, extendTrial, convertTrial } from '@/adminApi';
import { useAdmin } from '@/contexts/AdminContext';

export default function AdminTrialsPage() {
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();
  const [trials, setTrials] = useState<Record<string, unknown>[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 50 });
  const [filter, setFilter] = useState<'active' | 'expired'>('active');
  const [loading, setLoading] = useState(true);

  // Extend modal
  const [extendModal, setExtendModal] = useState<{ userId: string; email: string } | null>(null);
  const [extendDays, setExtendDays] = useState(7);
  const [extendLoading, setExtendLoading] = useState(false);

  // Convert modal
  const [convertModal, setConvertModal] = useState<{ userId: string; email: string } | null>(null);
  const [convertPlan, setConvertPlan] = useState('base');
  const [convertDays, setConvertDays] = useState(30);
  const [convertLoading, setConvertLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const toastRef = useRef<ReturnType<typeof setTimeout>>();
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), 3000);
  };

  const fetchTrials = useCallback(() => {
    setLoading(true);
    const fetcher = filter === 'active' ? getActiveTrials : getExpiredTrials;
    fetcher(pagination.page, pagination.limit)
      .then((res) => {
        setTrials(res.data.trials);
        setPagination({
          page: res.data.page,
          totalPages: Math.ceil(res.data.total / res.data.limit),
          total: res.data.total,
          limit: res.data.limit,
        });
      })
      .catch(() => setTrials([]))
      .finally(() => setLoading(false));
  }, [filter, pagination.page, pagination.limit]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchTrials();
  }, [isAuthenticated, navigate, fetchTrials]);

  const handleActivate = async (userId: string) => {
    try {
      await activateTrial(userId, 7);
      showToast('Триал активирован на 7 дней');
      fetchTrials();
    } catch (err) {
      showToast('Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'), 'error');
    }
  };

  const handleExtend = async () => {
    if (!extendModal) return;
    setExtendLoading(true);
    try {
      await extendTrial(extendModal.userId, extendDays);
      showToast(`Триал продлён на ${extendDays} дней`);
      setExtendModal(null);
      fetchTrials();
    } catch (err) {
      showToast('Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'), 'error');
    } finally { setExtendLoading(false); }
  };

  const handleConvert = async () => {
    if (!convertModal) return;
    setConvertLoading(true);
    try {
      await convertTrial(convertModal.userId, convertPlan, convertDays);
      showToast(`Триал конвертирован в подписку ${convertPlan}`);
      setConvertModal(null);
      fetchTrials();
    } catch (err) {
      showToast('Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'), 'error');
    } finally { setConvertLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {toast && <div className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg text-sm font-medium shadow-lg ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{toast.message}</div>}

      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/admin/dashboard" className="text-xl font-bold text-white hover:text-purple-400 transition">
            ← Назад
          </Link>
          <h1 className="text-xl font-bold text-white">Триалы ({pagination.total})</h1>
          <div />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => { setFilter('active'); setPagination((p) => ({ ...p, page: 1 })); }}
            className={`px-4 py-2 text-sm rounded-lg transition ${
              filter === 'active'
                ? 'bg-purple-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Активные
          </button>
          <button
            onClick={() => { setFilter('expired'); setPagination((p) => ({ ...p, page: 1 })); }}
            className={`px-4 py-2 text-sm rounded-lg transition ${
              filter === 'expired'
                ? 'bg-red-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Истёкшие
          </button>
        </div>

        {/* Table */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          {loading ? (
            <div className="px-6 py-12 text-center text-slate-500">Загрузка...</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                      <th className="px-6 py-3 font-medium">Email</th>
                      <th className="px-6 py-3 font-medium">Имя</th>
                      <th className="px-6 py-3 font-medium">Окончание триала</th>
                      <th className="px-6 py-3 font-medium">Статус</th>
                      <th className="px-6 py-3 font-medium">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trials.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                          Триалы не найдены
                        </td>
                      </tr>
                    ) : (
                      trials.map((trial: any) => {
                        const endDate = trial.trial_end_date ? new Date(trial.trial_end_date) : null;
                        const isExpired = endDate ? endDate < new Date() : true;

                        return (
                          <tr key={trial.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition">
                            <td className="px-6 py-3">
                              <Link to={`/admin/users/${trial.id}`} className="text-blue-400 hover:underline">
                                {trial.email}
                              </Link>
                            </td>
                            <td className="px-6 py-3 text-slate-300">{trial.full_name || '—'}</td>
                            <td className="px-6 py-3 text-slate-300 text-sm">
                              {endDate ? endDate.toLocaleDateString('ru-RU') : '—'}
                            </td>
                            <td className="px-6 py-3">
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  isExpired
                                    ? 'bg-red-600/30 text-red-300'
                                    : 'bg-blue-600/30 text-blue-300'
                                }`}
                              >
                                {isExpired ? 'Истёк' : 'Активен'}
                              </span>
                            </td>
                            <td className="px-6 py-3 space-x-2">
                              {filter === 'expired' && (
                                <button
                                  onClick={() => handleActivate(trial.id)}
                                  className="text-xs px-2 py-1 bg-blue-600/20 text-blue-400 rounded hover:bg-blue-600/40 transition"
                                >
                                  Активировать
                                </button>
                              )}
                              <button
                                onClick={() => setExtendModal({ userId: trial.id, email: trial.email })}
                                className="text-xs px-2 py-1 bg-green-600/20 text-green-400 rounded hover:bg-green-600/40 transition"
                              >
                                Продлить
                              </button>
                              <button
                                onClick={() => setConvertModal({ userId: trial.id, email: trial.email })}
                                className="text-xs px-2 py-1 bg-purple-600/20 text-purple-400 rounded hover:bg-purple-600/40 transition"
                              >
                                Конвертировать
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
                  <span className="text-sm text-slate-400">
                    Страница {pagination.page} из {pagination.totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      disabled={pagination.page <= 1}
                      onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                      className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 transition"
                    >
                      ←
                    </button>
                    <button
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                      className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded disabled:opacity-50 hover:bg-slate-600 transition"
                    >
                      →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Extend Modal */}
      {extendModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-md">
            <h2 className="text-lg font-bold text-white mb-4">Продлить триал: {extendModal.email}</h2>
            <div className="mb-4">
              <label className="block text-sm text-slate-400 mb-1">Количество дней</label>
              <input
                type="number"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 7)}
                min={1}
                max={365}
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setExtendModal(null)}
                className="px-4 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
              >
                Отмена
              </button>
              <button
                onClick={handleExtend}
                disabled={extendLoading}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                {extendLoading ? 'Сохранение...' : 'Продлить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert Modal */}
      {convertModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-md">
            <h2 className="text-lg font-bold text-white mb-4">Конвертировать: {convertModal.email}</h2>
            <div className="mb-4">
              <label className="block text-sm text-slate-400 mb-1">Тариф</label>
              <select
                value={convertPlan}
                onChange={(e) => setConvertPlan(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="base">Base</option>
                <option value="premium">Premium</option>
                <option value="pro">Pro</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm text-slate-400 mb-1">Длительность (дней)</label>
              <input
                type="number"
                value={convertDays}
                onChange={(e) => setConvertDays(parseInt(e.target.value) || 30)}
                min={1}
                max={365}
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConvertModal(null)}
                className="px-4 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
              >
                Отмена
              </button>
              <button
                onClick={handleConvert}
                disabled={convertLoading}
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
              >
                {convertLoading ? 'Сохранение...' : 'Конвертировать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
